/**
 * GSAP entrance/emphasis/exit/motion-path runtime for converted-PowerPoint `html` slides, shadow-scoped.
 *
 * A converted slide's animated shapes carry a declarative `data-aha-fx` descriptor (a JSON array of
 * PowerPoint effects mapped to a GSAP vocabulary — see aha-live-deck-mcp powerpoint/timing.ts FxSpec)
 * plus `data-step` (the click group). The published deck loader (aha-live-deck-mcp demo/aha-embed.js)
 * reconstructs the whole animation from those attributes alone; this is a faithful port of that
 * runtime into the presenter's Shadow-DOM casting view. It reads ONLY the already-sanitised DOM
 * attributes and drives GSAP itself — the fragment stays scriptless, so the stored-XSS surface the
 * `html` slide type hardened (./sanitize) is not reopened.
 *
 * GSAP animates dedicated CSS variables (--fx-x/-y/-s/-sx/-sy/-r) that FX_CSS composes IN FRONT of a
 * shape's own inline transform (PowerPoint rotation/flip), moved to --fx-base once, so an entrance
 * offset never destroys the shape's base transform and the settled frame is pixel-exact.
 */
/* eslint-disable no-param-reassign --
   This is a DOM animation engine: it attaches per-shape build state (el.__fx*) to the element and
   drives its inline transform/opacity/style directly, exactly as the published loader does. Mutating
   the passed element/state IS the work, so the rule is off for this file rather than aliased around. */
import { gsap } from 'gsap'

// The fixed slide frame — Fly In/Out travel off the named edge of this frame, the same distance for
// every shape regardless of its size/position (matches the loader's STAGE_W/STAGE_H).
const STAGE_W = 1280
const STAGE_H = 720

const FX_VARS = ['--fx-x', '--fx-y', '--fx-s', '--fx-sx', '--fx-sy', '--fx-r']
const FX_CLEAR_PROPS = FX_VARS.concat(['opacity', 'clipPath', 'transformOrigin']).join(',')
const FX_DUR = 0.55
const FX_EASE = 'power2.out'
const FX_EMPH_DUR = 0.9

// The composition rule + the "never hidden" guards, scoped to the shadow tree (the loader gates the
// same rule on html[data-aha-anim]; here the casting view only runs the JS below in the live-present
// view, so the rule is inert elsewhere because --fx-base/vars are never set and the shape's own
// inline transform wins). Print / reduced motion force every fx shape visible at rest.
export const FX_CSS = [
  '[data-aha-fx]{',
  'transform:translate(var(--fx-x,0px),var(--fx-y,0px)) ',
  'scale(var(--fx-sx,var(--fx-s,1)),var(--fx-sy,var(--fx-s,1))) ',
  'rotate(var(--fx-r,0deg)) var(--fx-base,);',
  'transform-origin:center;will-change:transform,opacity}',
  '@media (prefers-reduced-motion:reduce){[data-aha-fx]{opacity:1!important}}',
  '@media print{[data-aha-fx]{opacity:1!important;transform:var(--fx-base,none)!important;clip-path:none!important}}',
].join('')

function fxReduced() {
  try {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion:reduce)').matches
  } catch (e) {
    return false
  }
}
function fxPrint() {
  try {
    return window.matchMedia && window.matchMedia('print').matches
  } catch (e) {
    return false
  }
}

// True for a shape the converter animated (carries a data-aha-fx descriptor).
export function fxHasFx(el) {
  return !!(el && el.getAttribute && el.getAttribute('data-aha-fx'))
}

// Move a shape's own inline transform into --fx-base once so the composition rule (lower priority
// than inline) can take over, and drop the pre-paint data-fx-hide FOUC guard now that GSAP owns the
// shape's opacity. Caches the original transform to restore it pixel-exact on settle.
function fxEnsureBase(el) {
  if (el.__fxInit) return
  el.__fxInit = true
  const t = el.style.transform || ''
  el.__fxBase = t
  if (t) el.style.setProperty('--fx-base', t)
  el.style.transform = ''
  el.removeAttribute('data-fx-hide')
}

// Clear every fx var/inline override GSAP set and restore the shape's ORIGINAL transform — the
// settled, natural state matching the static gold.
function fxSettle(el) {
  fxEnsureBase(el)
  // Kill any live tween BEFORE clearing the vars. An INFINITE loop (rep:-1 — the canonical float, or an
  // infinite emphasis) never fires onComplete, so the non-present views (filmstrip / thumbnail / PDF /
  // back-nav) settle it through this path; without the kill, GSAP 3's default overwrite is off, so the
  // still-live tween re-applies --fx-x/--fx-y the next frame and the shape keeps bobbing instead of
  // coming to rest.
  gsap.killTweensOf(el)
  gsap.set(el, { clearProps: FX_CLEAR_PROPS })
  el.style.transform = el.__fxBase || ''
}

// The hidden / start state an entrance tweens FROM (in fx-var space, composed over the base).
function fxStart(spec) {
  const v = { opacity: 0 }
  const { k } = spec
  const d = spec.dir
  if (k === 'fly') {
    const fd = d || ''
    if (fd.indexOf('l') >= 0) v['--fx-x'] = `${-STAGE_W}px`
    else if (fd.indexOf('r') >= 0) v['--fx-x'] = `${STAGE_W}px`
    if (fd.indexOf('u') >= 0) v['--fx-y'] = `${-STAGE_H}px`
    else if (fd.indexOf('d') >= 0) v['--fx-y'] = `${STAGE_H}px`
  } else if (k === 'zoom') {
    v['--fx-s'] = 0.1
  } else if (k === 'grow') {
    v['--fx-s'] = 0.1
    v['--fx-r'] = '-30deg'
  } else if (k === 'split') {
    v['--fx-sx'] = 0.02
  } else if (k === 'wipe') {
    const wd = d || ''
    let t = 0
    let r = 0
    let b = 0
    let l = 0
    if (wd.indexOf('u') >= 0) t = 100
    if (wd.indexOf('r') >= 0) r = 100
    if (wd.indexOf('d') >= 0) b = 100
    if (wd.indexOf('l') >= 0) l = 100
    if (!t && !r && !b && !l) r = 100
    v.clipPath = `inset(${t}% ${r}% ${b}% ${l}%)`
  }
  // A rotating entrance (PowerPoint Center Revolve, preset 31) starts turned by its authored angle and
  // settles to 0deg; overrides a kind's own default (grow's -30deg) when the deck states its own angle.
  if (spec.f === 'entr' && spec.rot != null) v['--fx-r'] = `${spec.rot}deg`
  return v
}

// The revealed / end state (identity offset), tweened TO. clipPath rides along only for a wipe — GSAP
// has no matching "from" for an inset() when the live clip-path is the default "none" (every other
// kind), so it would warn on an invalid tween for no visual effect.
function fxEnd(spec) {
  const v = {
    opacity: 1, '--fx-x': '0px', '--fx-y': '0px', '--fx-s': 1, '--fx-sx': 1, '--fx-sy': 1, '--fx-r': '0deg',
  }
  if (spec && spec.k === 'wipe') v.clipPath = 'inset(0% 0% 0% 0%)'
  return v
}

// Put an entrance at its hidden start (instant) — slide-enter and back-press retract.
function fxHide(el, spec) {
  fxEnsureBase(el)
  if (fxReduced() || fxPrint()) {
    el.style.opacity = ''
    return
  }
  gsap.killTweensOf(el)
  el.style.transform = ''
  gsap.set(el, fxStart(spec))
}

// Reveal an entrance: tween (a real press) or snap (bulk enter / reduced motion).
function fxReveal(el, spec, animate) {
  fxEnsureBase(el)
  if (fxPrint()) {
    fxSettle(el)
    return
  }
  gsap.killTweensOf(el)
  if (!animate || fxReduced()) {
    fxSettle(el)
    return
  }
  el.style.transform = ''
  const to = Object.assign(fxEnd(spec), {
    duration: spec.dur || FX_DUR,
    ease: spec.ease || FX_EASE,
    delay: spec.d || 0,
    onComplete() { fxSettle(el) },
  })
  if (spec.rep) {
    to.repeat = spec.rep
    if (spec.yoyo) to.yoyo = true
  }
  gsap.fromTo(el, fxStart(spec), to)
}

// Play a one-shot emphasis cue that leaves the shape back at rest.
function fxEmph(el, spec) {
  fxEnsureBase(el)
  if (fxReduced() || fxPrint()) return
  gsap.killTweensOf(el)
  const done = () => fxSettle(el)
  const dur = spec.dur || FX_EMPH_DUR
  const delay = spec.d || 0
  const rep = (o, defRep, defYoyo) => {
    o.repeat = spec.rep != null ? spec.rep : defRep
    if (spec.yoyo != null ? spec.yoyo : defYoyo) o.yoyo = true
    return o
  }
  if (spec.k === 'spin') {
    if (spec.ox != null) el.style.transformOrigin = `calc(50% + ${spec.ox}px) calc(50% + ${spec.oy}px)`
    const spinTo = spec.rot != null ? spec.rot : 360
    gsap.fromTo(el, { '--fx-r': '0deg' }, rep({
      '--fx-r': `${spinTo}deg`, duration: dur, ease: spec.ease || 'none', delay, onComplete: done,
    }, 0, false))
  } else if (spec.k === 'pulse') {
    gsap.fromTo(el, { '--fx-s': 1 }, rep({
      '--fx-s': 1.12, duration: dur / 2, ease: spec.ease || 'sine.inOut', delay, onComplete: done,
    }, 1, true))
  } else if (spec.k === 'flash') {
    gsap.fromTo(el, { opacity: 1 }, rep({
      opacity: 0.25, duration: dur / 4, ease: spec.ease || 'none', delay, onComplete: done,
    }, 3, true))
  } else {
    gsap.fromTo(el, { opacity: 1 }, rep({
      opacity: 0.35, duration: dur / 2, ease: spec.ease || 'sine.inOut', delay, onComplete: done,
    }, 1, true))
  }
}

// Play an EXIT: the shape animates OUT and STAYS gone in the live view — no settle-back.
function fxExit(el, spec) {
  fxEnsureBase(el)
  if (fxReduced() || fxPrint()) {
    fxSettle(el)
    return
  }
  gsap.killTweensOf(el)
  el.style.transform = ''
  // Reset EVERY transform var (scale / split-scale / rotation / wipe-clip), not just x/y, in both the
  // from and the to: a killTweensOf-interrupted entrance leaves a stale --fx-s/--fx-r/clipPath, so an
  // exit that only reset x/y would fade out from a half-built scale or rotation. Every prop appears in
  // both endpoints so each is a clean tween to rest (the fly block then overrides x/y to the edge).
  const rest = {
    '--fx-x': '0px', '--fx-y': '0px', '--fx-s': 1, '--fx-sx': 1, '--fx-sy': 1, '--fx-r': '0deg', clipPath: 'inset(0% 0% 0% 0%)',
  }
  const to = {
    ...rest, opacity: 0, duration: spec.dur || FX_DUR, ease: spec.ease || FX_EASE, delay: spec.d || 0,
  }
  if (spec.k === 'fly') {
    const d = spec.dir || ''
    if (d.indexOf('l') >= 0) to['--fx-x'] = `${-STAGE_W}px`
    else if (d.indexOf('r') >= 0) to['--fx-x'] = `${STAGE_W}px`
    if (d.indexOf('u') >= 0) to['--fx-y'] = `${-STAGE_H}px`
    else if (d.indexOf('d') >= 0) to['--fx-y'] = `${STAGE_H}px`
  }
  gsap.fromTo(el, { ...rest, opacity: 1 }, to)
}

// Reverse an exit on a back-press: bring the shape back in from its edge to rest, then settle.
function fxUnexit(el, spec) {
  fxEnsureBase(el)
  if (fxReduced() || fxPrint()) {
    fxSettle(el)
    return
  }
  gsap.killTweensOf(el)
  gsap.fromTo(el, fxStart({ f: 'entr', k: spec.k === 'fly' ? 'fly' : 'fade', dir: spec.dir }),
    Object.assign(fxEnd(spec), {
      duration: spec.dur || FX_DUR, ease: spec.ease || FX_EASE, onComplete() { fxSettle(el) },
    }))
}

// Play a MOTION PATH: the shape translates from rest to (spec.dx, spec.dy) px and, with the authored
// auto-reverse/repeat, oscillates and returns — a bobbing cloud floats on --fx-y, NOT a scale. It
// always ends back at rest (onComplete settle), matching the static gold. Never runs under print /
// reduced motion (those leave the shape at its authored position).
function fxPath(el, spec) {
  fxEnsureBase(el)
  if (fxReduced() || fxPrint()) {
    fxSettle(el)
    return
  }
  gsap.killTweensOf(el)
  el.style.transform = ''
  // Reset the FULL rest frame (scale / rotation / wipe-clip) in both endpoints, not just x/y — same as
  // fxExit: a killTweensOf-interrupted zoom/grow/wipe entrance would otherwise float at a stale scale/
  // angle/clip. Only x/y move; the rest stay pinned at rest through the (possibly yoyo/looping) bob.
  const rest = {
    '--fx-s': 1, '--fx-sx': 1, '--fx-sy': 1, '--fx-r': '0deg', clipPath: 'inset(0% 0% 0% 0%)',
  }
  const to = {
    ...rest,
    '--fx-x': `${spec.dx || 0}px`,
    '--fx-y': `${spec.dy || 0}px`,
    duration: spec.dur || FX_DUR,
    ease: spec.ease || FX_EASE,
    delay: spec.d || 0,
    onComplete() { fxSettle(el) },
  }
  if (spec.rep) {
    to.repeat = spec.rep
    if (spec.yoyo) to.yoyo = true
  }
  gsap.fromTo(el, { ...rest, '--fx-x': '0px', '--fx-y': '0px' }, to)
}

// The shape's effect LIST — one entry per PowerPoint effect (entrance and/or emphasis and/or exit),
// each carrying its own build step `s`. data-aha-fx is a JSON array; a bare object is normalised to a
// one-element list, and any spec missing `s` inherits the element's data-step.
function fxSpecOf(el) {
  if (el.__fxSpec !== undefined) return el.__fxSpec
  let list = null
  const raw = el.getAttribute('data-aha-fx')
  if (raw) {
    try {
      const parsed = JSON.parse(raw)
      const arr = (Array.isArray(parsed) ? parsed : [parsed]).filter(Boolean)
      if (arr.length) {
        const baseStep = +el.getAttribute('data-step') || 0
        list = arr.map((sp) => (sp.s == null ? ({ ...sp, s: baseStep }) : sp))
      }
    } catch (e) {
      list = null
    }
  }
  el.__fxSpec = list
  return list
}

// The highest build step any of the shape's effects sits at — a shape's exit sits at a LATER step
// than its entrance (data-step), so the stepper must reach it. 0 when the shape carries no fx.
export function fxMaxStepFor(el) {
  const list = fxSpecOf(el)
  if (!list) return 0
  let max = 0
  for (let i = 0; i < list.length; i += 1) {
    const s = +list[i].s || 0
    if (s > max) max = s
  }
  return max
}

// Apply the shown/hidden state to one spec, given whether this is an animated press or a bulk enter.
// Entrances reveal/hide; emphasis fires its one-shot the first forward time; exits play out then stay
// gone (but a bulk/rest pass settles them VISIBLE, matching the static gold). `st` is the spec's own
// state slot within the element's list.
function fxApplyOne(el, spec, shown, animate, st) {
  if (spec.f === 'emph') {
    if (shown && !st.played) {
      st.played = true
      if (animate) fxEmph(el, spec)
    } else if (!shown) {
      st.played = false
    }
    return
  }
  if (spec.f === 'exit') {
    if (shown && animate && !st.exited) {
      st.exited = true
      fxExit(el, spec)
    } else if (shown && !animate) {
      st.exited = false
      fxSettle(el)
    } else if (!shown && st.exited) {
      st.exited = false
      fxUnexit(el, spec)
    }
    return
  }
  if (spec.f === 'path') {
    // A motion path is VISIBLE at its authored position at rest; only a live forward-press past its
    // step plays the move (which oscillates back). A bulk / back pass settles it at rest.
    if (shown && animate && !st.played) {
      st.played = true
      fxPath(el, spec)
    } else if (shown && !animate) {
      st.played = false
      fxSettle(el)
    } else if (!shown) {
      if (st.played) fxSettle(el)
      st.played = false
    }
    return
  }
  const was = !!st.shown
  if (shown && !was) {
    st.shown = true
    fxReveal(el, spec, animate)
  } else if (!shown && was) {
    st.shown = false
    fxHide(el, spec)
  } else if (shown && !animate) {
    st.shown = true
    fxReveal(el, spec, false)
  } else if (!shown && !was) {
    fxHide(el, spec)
  }
}

// Kill every live GSAP tween this runtime started for a scope's fx shapes. An infinite float/emphasis
// (repeat:-1) never fires onComplete, so its tween keeps ticking on nodes that are about to be — or
// already are — detached; the casting view calls this BEFORE it swaps the shadow content and on
// teardown so those loops can't accumulate across a presentation. Scoped to this scope's own
// [data-aha-fx] targets (the only elements fx* ever tweens) — never a global gsap.killTweensOf() that
// would reach unrelated app tweens.
export function fxKillAll(scope) {
  if (!scope || !scope.querySelectorAll) return
  scope.querySelectorAll('[data-aha-fx]').forEach((el) => { gsap.killTweensOf(el) })
}

// Drive one shape's whole effect list at the current build step `cur`: each spec is shown/played when
// its own step has landed (s <= cur). `animate` is true for a real forward/back press, false on a
// bulk slide-enter (snap to the settled state). The list is ordered entrance-first, exit-last.
export function fxApply(el, cur, animate) {
  const list = fxSpecOf(el)
  if (!list) return
  const state = el.__fxState || (el.__fxState = [])
  for (let i = 0; i < list.length; i += 1) {
    const spec = list[i]
    fxApplyOne(el, spec, (spec.s || 0) <= cur, animate, state[i] || (state[i] = {}))
  }
}
